/**
 * AskEma Widget — floating chatbot-like experience for learners.
 *
 * Provides access to:
 * - Ask a question about the training topic (Ask an Expert)
 * - Browse FAQs / Help Center
 * - Contact support
 */

import { useState, useMemo, useCallback } from 'react';
import type { CSSProperties } from 'react';
import type { ThemeTokens } from '@course-authoring/shared-types';
import { useAssistant, useTheme, useAccountConfig } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { useIsMobileViewport } from '../hooks/useIsMobileViewport';
import { AskEmaPanel } from './AskEmaPanel';
import type { ContactSupportSubmission } from './ContactSupportForm';

export interface AskEmaWidgetProps {
  /** Overrides the account config's `enableAskExpert` when provided. */
  askExpertEnabled?: boolean;
  learnerName?: string;
  learnerEmail?: string;
  isPreview?: boolean;
  courseId?: string;
  lessonId?: string;
  blockId?: string;
  onAskExpertSubmit?: (data: { question: string; email: string }) => Promise<void> | void;
  onContactSupportSubmit?: (data: ContactSupportSubmission) => Promise<void> | void;
}

function createHoverCSS(): string {
  return `
    .ema-fab-trigger:hover {
      box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25) !important;
    }
  `;
}

function createStyles(tokens: ThemeTokens, isCompact: boolean): Record<string, CSSProperties> {
  return {
    trigger: {
      position: 'absolute',
      bottom: tokens.spacing.lg,
      right: tokens.spacing.lg,
      zIndex: 9000,
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      // Compact: icon-only FAB, equal padding for a compact square
      padding: isCompact ? '13px' : `13px 21px`,
      backgroundColor: tokens.brand.accent,
      color: tokens.text.onAccent,
      border: 'none',
      borderRadius: '8.509px',
      fontFamily: tokens.typography.fontFamily,
      fontSize: '17px',
      fontWeight: tokens.typography.weights.bold,
      cursor: 'pointer',
      transition: `box-shadow ${tokens.ui.transitionDefault}`,
    },
  };
}

export function AskEmaWidget({
  askExpertEnabled,
  learnerName,
  learnerEmail,
  isPreview = false,
  courseId,
  lessonId,
  blockId,
  onAskExpertSubmit,
  onContactSupportSubmit,
}: AskEmaWidgetProps) {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const { assistant } = useAssistant();
  const { enableAskExpert } = useAccountConfig();
  const effectiveAskExpertEnabled = askExpertEnabled ?? enableAskExpert;
  // Collapse below the width where the pill starts crowding content with the
  // inline nav sidebar open (mobile-width content next to the min-width
  // sidebar), and stay collapsed at every width under that — no popping back
  // to the pill when the nav folds behind the hamburger and frees the width.
  const compactBreakpoint =
    tokens.layout.mobileBreakpoint + parseFloat(tokens.layout.sidebar.minWidth);
  const isCompact = useIsMobileViewport(compactBreakpoint);
  const styles = useMemo(() => createStyles(tokens, isCompact), [tokens, isCompact]);
  const hoverCSS = useMemo(() => createHoverCSS(), []);
  const [isOpen, setIsOpen] = useState(false);
  const [activeView, setActiveView] = useState<'menu' | 'ask-expert' | 'contact-support'>('menu');

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) setActiveView('menu');
      return !prev;
    });
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setActiveView('menu');
  }, []);

  const handleSelectAskExpert = useCallback(() => {
    setActiveView('ask-expert');
  }, []);

  const handleContactSupport = useCallback(() => {
    setActiveView('contact-support');
  }, []);

  const handleBack = useCallback(() => {
    setActiveView('menu');
  }, []);

  return (
    <>
      {!isOpen && (
        <>
          <style>{hoverCSS}</style>
          <button
            type="button"
            className="ema-fab-trigger"
            onClick={handleToggle}
            style={styles.trigger}
            aria-label={t('ask_ema.title', { assistantName: assistant.name })}
            data-testid="ask-ema-trigger"
          >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M0 13.2958V3.98874C0 1.78662 1.78662 0 3.98874 0H17.2846C19.4867 0 21.2733 1.78662 21.2733 3.98874V13.2958C21.2733 15.4979 19.4867 17.2846 17.2846 17.2846H12.2986C12.0826 17.2846 11.8748 17.3552 11.7003 17.484L6.91382 21.0739C6.73931 21.2027 6.53157 21.2733 6.31551 21.2733C5.7629 21.2733 5.31832 20.8287 5.31832 20.2761V17.2846H3.98874C1.78662 17.2846 0 15.4979 0 13.2958Z" fill="currentColor" />
          </svg>
          {!isCompact && t('ask_ema.title', { assistantName: assistant.name })}
          </button>
        </>
      )}

      {isOpen && (
        <AskEmaPanel
          activeView={activeView}
          askExpertEnabled={effectiveAskExpertEnabled}
          learnerName={learnerName}
          learnerEmail={learnerEmail}
          isPreview={isPreview}
          courseId={courseId}
          lessonId={lessonId}
          blockId={blockId}
          onContactSupport={handleContactSupport}
          onContactSupportSubmit={onContactSupportSubmit}
          onSelectAskExpert={handleSelectAskExpert}
          onAskExpertSubmit={onAskExpertSubmit}
          onBack={handleBack}
          onClose={handleClose}
        />
      )}
    </>
  );
}
